import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// All nav items with permission key (null = main admin only)
const NAV_ITEMS = [
  { path: '/admin/dashboard',    icon: 'fa-chart-pie',           label: 'Dashboard',     permission: null },
  { path: '/admin/payments',     icon: 'fa-indian-rupee-sign',   label: 'Payments',      permission: 'payments',     badge: 'pending' },
  { path: '/admin/revenue',      icon: 'fa-chart-line',          label: 'Revenue',        permission: null },
  { path: '/admin/doctors',      icon: 'fa-user-doctor',         label: 'Doctors',        permission: 'doctors' },
  { path: '/admin/patients',     icon: 'fa-users',               label: 'Patients',       permission: 'patients' },
  { path: '/admin/appointments', icon: 'fa-calendar-alt',        label: 'Appointments',   permission: 'appointments' },
  { path: '/admin/staff',        icon: 'fa-user-shield',         label: 'Admin Staff',    permission: null },
  { path: '/admin/settings',     icon: 'fa-gear',                label: 'Settings',       permission: null },
];

export default function AdminSidebar({ pendingPayments = 0 }) {
  const { pathname }      = useLocation();
  const navigate          = useNavigate();
  const { logout, user }  = useAuth();

  const isAdminStaff  = user?.isAdminStaff === true;
  const staffPerms    = user?.permissions || {};

  // Filter nav items for admin staff
  const visibleItems = NAV_ITEMS.filter(item => {
    if (!isAdminStaff) return true; // main admin sees all
    if (item.permission === null) return false; // staff cannot see dashboard/revenue/staff/settings
    return staffPerms[item.permission] === true;
  });

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-60 bg-white border-r border-border z-40 flex flex-col">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-border">
        <span className="text-xl font-bold text-primary font-heading">Faristaa</span>
        <p className="text-[10px] text-text-muted mt-0.5">
          {isAdminStaff ? 'Admin Staff Panel' : 'Admin Panel'}
        </p>
      </div>

      {/* User info */}
      <div className="px-4 py-3 border-b border-border bg-surface">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isAdminStaff ? 'bg-amber-50' : 'bg-primary-light'
          }`}>
            <i className={`fa-solid ${isAdminStaff ? 'fa-user-tie text-amber-600' : 'fa-shield-halved text-primary'} text-sm`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-text-primary truncate">
              {user?.name || user?.adminName || 'Admin'}
            </p>
            <p className="text-[10px] text-text-muted">
              {isAdminStaff ? 'Staff Member' : 'Administrator'}
            </p>
          </div>
        </div>
        {/* Staff permission badges */}
        {isAdminStaff && (
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(staffPerms).filter(([, v]) => v).map(([key]) => (
              <span key={key} className="text-[9px] font-bold bg-primary-light text-primary border border-primary/20 px-1.5 py-0.5 rounded-full capitalize">
                {key}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => {
          const active = pathname.startsWith(item.path);
          return (
            <button key={item.path} onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                active ? 'bg-primary text-white' : 'text-text-secondary hover:bg-surface hover:text-text-primary'
              }`}>
              <i className={`fa-solid ${item.icon} text-sm w-4`} />
              <span className="text-sm font-medium flex-1">{item.label}</span>
              {item.badge === 'pending' && pendingPayments > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  active ? 'bg-white/30 text-white' : 'bg-danger text-white'
                }`}>
                  {pendingPayments > 99 ? '99+' : pendingPayments}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 pb-4 border-t border-border pt-3">
        <button onClick={() => { logout(); navigate('/'); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-danger hover:bg-red-50 transition-colors">
          <i className="fa-solid fa-right-from-bracket text-sm w-4" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </aside>
  );
}
